import { useEffect, useState } from "react";
import { Image } from "react-bootstrap";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import "./careerGuidanceHomepage.css";
import { BlobImage } from "../../../utils/blobImage";
import { serverConfig } from "../../../services/server-config";
import { CandidateService } from "../../../services/candidateService";
import { CareerGuidanceService } from "../../../services/careerGuidanceService";
import { CandidatePreLoginService } from "../../../services/candidatePreLoginService";
import { CandidatePostLoginService } from "../../../services/candidatePostLoginService";


const CareerGuidanceImage = ({ imageUrl, alt }) => {
    const [imageSrc, setImageSrc] = useState(null);
    const [imageLoading, setImageLoading] = useState(true);

    useEffect(() => {
        let objectUrl = null;
        let cancelled = false;

        const fetchImage = async () => {
            console.log("Loading Career Guidance image:", imageUrl);

            if (!imageUrl) {
                setImageLoading(false);
                return;
            }

            if (imageUrl.startsWith("http")) {
                console.log("Direct image URL:", imageUrl);
                setImageSrc(imageUrl);
                setImageLoading(false);
                return;
            }

            try {
                setImageLoading(true);

                const response =
                    await CareerGuidanceService.getPublicCareerGuidanceImage(
                        imageUrl
                    );

                const blob = response.data;

                console.log("Image blob:", blob.type, blob.size);

                if (cancelled) return;

                objectUrl = URL.createObjectURL(blob);

                setImageSrc(objectUrl);
            } catch (error) {
                console.error(
                    "Career Guidance image loading failed:",
                    error
                );
            } finally {
                if (!cancelled) {
                    setImageLoading(false);
                }
            }
        };

        fetchImage();

        return () => {
            cancelled = true;

            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        };
    }, [imageUrl]);

    return (
        <div
            style={{
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "#f5f5f5"
            }}
        >
            {imageLoading && (
                <span style={{ fontSize: "12px", color: "#999" }}>
                    Loading...
                </span>
            )}

            {!imageLoading && imageSrc && (
                <img
                    src={imageSrc}
                    alt={alt || "Career Guidance"}
                    style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover"
                    }}
                    decoding="async"
                />
            )}

            {!imageLoading && !imageSrc && (
                <span style={{ fontSize: "12px", color: "#999" }}>
                    No Image
                </span>
            )}
        </div>
    );
};

const CareerGuidanceHomepage = () => {
    const [loader, setLoader] = useState(false);
    const [slotLoader, setSlotLoader] = useState(false);
    const [sessions, setSessions] = useState([]);
    const [sessionPageNo, setSessionPageNo] = useState(0);
    const [sessionPageCount, setSessionPageCount] = useState(0);
    const [paginationLoading, setPaginationLoading] = useState(false);
    const [showCandidateModal, setShowCandidateModal] = useState(false);
    const [showSlotModal, setShowSlotModal] = useState(false);
    const [slots, setSlots] = useState([]);
    const [selectedSession, setSelectedSession] = useState(null);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [loadingSessionId, setLoadingSessionId] = useState(null);
    const [candidateDetails, setCandidateDetails] = useState({
        firstName: "",
        lastName: "",
        email: "",
        phoneNumber: "",
        password: "",
    });

    const [candidateErrors, setCandidateErrors] = useState({});
    const [showPassword, setShowPassword] = useState(false);

    const [showPaymentSuccessModal, setShowPaymentSuccessModal] = useState(false);

    const fetchSessions = async (page = 0) => {
        setLoader(true);

        try {
            const response =
                await CareerGuidanceService.getPublicSessions(page, 8);

            const data = response?.data;

            setSessions(data?.sessions || []);
            setSessionPageCount(data?.pageCount || 0);

        } catch (error) {
            console.error(
                "Career Guidance Sessions API Error:",
                error
            );

            setSessions([]);
            setSessionPageCount(0);

        } finally {
            setLoader(false);
        }
    };

    useEffect(() => {
        fetchSessions(sessionPageNo);
    }, [sessionPageNo]);

    const fetchSlots = async (sessionId) => {
        setSlotLoader(true);

        try {
            const response =
                await CareerGuidanceService.getPublicSlots(
                    sessionId,
                    0,
                    10
                );

            const data = response?.data;

            setSlots(data?.slots || []);
            setSelectedSlot(null);
            setShowSlotModal(true);

        } catch (error) {
            console.error(
                "Career Guidance Slots API Error:",
                error
            );

            setSlots([]);

            alert(
                error?.response?.data?.message ||
                "Unable to load available slots. Please try again."
            );

        } finally {
            setSlotLoader(false);
            setLoadingSessionId(null);
        }
    };

    const handleBuy = (session) => {

        setSelectedSession(session);

        setCandidateDetails({
            firstName: "",
            lastName: "",
            email: "",
            phoneNumber: "",
            password: "",
        });

        setCandidateErrors({});

        if (session.bookingType === "SLOT_BASED") {
            setLoadingSessionId(session.id);
            fetchSlots(session.id);
        } else {
            setShowCandidateModal(true);
        }
    };

    const validate = (name, value) => {
        let error = "";

        if (name === "firstName" || name === "lastName") {
            if (!/^[A-Za-z ]+$/.test(value)) {
                error = "Name should contain only letters and spaces";
            }
        }

        if (name === "phoneNumber") {
            if (/^0/.test(value)) {
                error = "Mobile number should not start with 0";
            } else if (!/^\+?[0-9]{2}[0-9]{10}$/.test(value)) {
                error = "Enter a valid mobile number (+919876XXXXX0)";
            }
        }

        if (name === "email") {
            if (
                !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value)
            ) {
                error = "Enter a valid email address";
            }
        }

        if (name === "password") {
            if (value.length < 8) {
                error = "Password must be at least 8 characters";
            } else if (!/[@$&!#]/.test(value)) {
                error = "Password must contain at least one special character (@ $ & ! #)";
            }
        }

        return error;
    };

    const handleCandidateChange = (e) => {
        const { name, value } = e.target;

        let newValue = value;

        if (name === "phoneNumber") {
            newValue = newValue.replace(/^0+/, '');

            if (!newValue.startsWith("+")) {
                newValue = "+91" + newValue;
            }
        }

        const fieldError = validate(name, newValue);

        setCandidateDetails({
            ...candidateDetails,
            [name]: newValue,
        });

        setCandidateErrors({
            ...candidateErrors,
            [name]: fieldError,
        });
    };

    const validateCandidateDetails = () => {
        const newErrors = {};

        Object.keys(candidateDetails).forEach((key) => {
            newErrors[key] = validate(key, candidateDetails[key]);
        });

        setCandidateErrors(newErrors);

        return !Object.values(newErrors).some(
            (error) => error !== ""
        );
    };

    const logoutCandidate = async () => {
        try {
            await CandidateService.logout();
        } catch (error) {
            console.error("Logout failed:", error);
        } finally {
            localStorage.removeItem("authToken");
        }
    };

    const handleCandidateSubmit = async (e) => {
        e.preventDefault();

        const isValid = validateCandidateDetails();

        if (!isValid) {
            alert("Please fix form errors before submitting.");
            return;
        }

        if (!selectedSession) {
            alert("Session details are missing.");
            return;
        }

        if (
            selectedSession.bookingType === "SLOT_BASED" &&
            !selectedSlot
        ) {
            alert("Please select a slot.");
            return;
        }

        const payload = {
            firstName: candidateDetails.firstName.trim(),
            lastName: candidateDetails.lastName.trim(),
            primaryPhoneNumber: candidateDetails.phoneNumber.trim(),
            primaryEmailId: candidateDetails.email.trim(),
            password: candidateDetails.password,
            source: "CAREER_GUIDANCE",
        };

        try {
            const registrationResponse =
                await CandidatePreLoginService.candidateRegistration(payload);


            const loginResponse = await CandidateService.login(
                registrationResponse?.data?.candidateId,
                candidateDetails.password,
                "",
                "",
                ""
            );

            const accessToken = loginResponse?.data?.accessToken;

            if (!accessToken) {
                alert("Unable to authenticate candidate.");
                return;
            }

            localStorage.setItem("authToken", accessToken);

            const request = {
                sessionId: selectedSession.id,
                gateway: "RAZORPAY",
                ...(selectedSession.bookingType === "SLOT_BASED" && {
                    slotId: selectedSlot.id,
                }),
            };

            console.log("Purchase Session Request:", request);

            const response =
                await CareerGuidanceService.purchaseSession(request);

            console.log("Purchase Session Response:", response);

            if (
                response.status !== 200 &&
                response.status !== 201 &&
                response.status !== 202
            ) {
                alert("Unable to initiate payment.");
                await logoutCandidate();
                return;
            }

            const order =
                response?.data?.paymentGatewayDTO?.razorpayOrder;

            const transactionId =
                response?.data?.transactionId;

            if (!order?.orderId) {
                alert("Unable to create payment order.");
                await logoutCandidate();
                return;
            }

            setShowCandidateModal(false);

            const options = {
                key: serverConfig.razorPayKey,

                amount: order.amount,
                currency: order.currency,

                name: "Leaderly",

                description:
                    selectedSession.headline ||
                    "Career Guidance Session",

                order_id: order.orderId,

                prefill: {
                    name:
                        `${candidateDetails.firstName} ${candidateDetails.lastName}`
                            .trim(),

                    email: candidateDetails.email,

                    contact: candidateDetails.phoneNumber,
                },

                notes: {
                    sessionId: selectedSession.id,

                    ...(selectedSession.bookingType === "SLOT_BASED" && {
                        slotId: selectedSlot.id,
                    }),

                    ...(transactionId && {
                        transactionId: transactionId,
                    }),
                },

                theme: {
                    color: "#F35824",
                },

                handler: async function (paymentResponse) {
                    console.log(
                        "RAZORPAY PAYMENT RESPONSE:",
                        paymentResponse
                    );

                    try {
                        const verifyPayload = {
                            razorpay_payment_id:
                                paymentResponse.razorpay_payment_id,

                            razorpay_order_id:
                                paymentResponse.razorpay_order_id,

                            razorpay_signature:
                                paymentResponse.razorpay_signature,
                        };

                        const verifyResponse =
                            await CandidatePostLoginService.verifyPayment(
                                verifyPayload
                            );

                        console.log(
                            "VERIFY PAYMENT RESPONSE:",
                            verifyResponse
                        );

                        if (
                            verifyResponse.status === 200 ||
                            verifyResponse.status === 201 ||
                            verifyResponse.status === 202
                        ) {
                            setShowPaymentSuccessModal(true);

                            setSelectedSlot(null);
                            setSelectedSession(null);

                            await logoutCandidate();
                        } else {
                            alert(
                                "Payment was successful, but verification is still pending."
                            );
                        }
                    } catch (err) {
                        console.error(
                            "Payment verification failed:",
                            err
                        );

                        alert(
                            "Payment was successful. Booking confirmation is being processed."
                        );
                        await logoutCandidate();
                    }
                },

                modal: {
                    ondismiss: async function () {
                        console.log(
                            "Razorpay payment window closed."
                        );

                        await logoutCandidate();
                    },
                },
            };

            const rzp = new window.Razorpay(options);

            rzp.on("payment.failed", async function (response) {
                console.error(
                    "Razorpay Payment Failed:",
                    response
                );

                alert(
                    response?.error?.description ||
                    "Payment failed. Please try again."
                );

                await logoutCandidate();
            });

            rzp.open();

        } catch (error) {
            console.error(
                "Unable to continue:",
                error
            );

            const fieldViolations =
                error?.response?.data?.fieldViolations;

            const message =
                error?.response?.data?.message || "";

            if (
                fieldViolations &&
                typeof fieldViolations === "object"
            ) {
                Object.keys(fieldViolations).forEach((field) => {
                    let errorMessage = fieldViolations[field];

                    if (field === "password") {
                        errorMessage =
                            "Password must contain at least one special character (@ $ & ! #)";
                    }

                    setCandidateErrors((prev) => ({
                        ...prev,
                        [field]: errorMessage,
                    }));
                });

                return;
            } else if (message.toLowerCase().includes("email")) {
                alert(
                    "Duplicate email ID found. Please use a different one."
                );
            } else if (
                message.toLowerCase().includes("phone") ||
                message.toLowerCase().includes("mobile")
            ) {
                alert(
                    "Duplicate mobile number found. Please give a valid one."
                );
            } else {
                alert(
                    message ||
                    "Unable to continue. Please try again."
                );
            }
        }
    };

    const closeCandidateModal = () => {

        setShowCandidateModal(false);

        setCandidateErrors({});
    };

    const closeSlotModal = () => {

        setShowSlotModal(false);

        setSelectedSlot(null);

        setSelectedSession(null);
    };


    const handlePagination = (page) => {
        setSessionPageNo(page);
    };

    const formatDateTime = (timestamp) => {

        if (!timestamp) {
            return "";
        }

        return new Date(timestamp).toLocaleString(
            "en-IN",
            {
                day: "2-digit",
                month: "short",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                hour12: true,
            }
        );
    };

    const renderPageNumbers = () => {

        const currentPage = sessionPageNo + 1;
        const pageNumbers = [];

        if (sessionPageCount <= 0) {
            return pageNumbers;
        }

        pageNumbers.push(1);

        if (sessionPageCount > 5) {

            if (currentPage > 3) {
                pageNumbers.push('...');
            }

            const start = Math.max(2, currentPage - 1);
            const end = Math.min(
                sessionPageCount - 1,
                currentPage + 1
            );

            for (let i = start; i <= end; i++) {
                pageNumbers.push(i);
            }

            if (currentPage < sessionPageCount - 2) {
                pageNumbers.push('...');
            }

            pageNumbers.push(sessionPageCount);

        } else {

            for (let i = 2; i <= sessionPageCount; i++) {
                pageNumbers.push(i);
            }

        }

        return pageNumbers;

    };

    const renderPagination = () => {

        if (sessionPageCount <= 1) {
            return null;
        }

        return (
            <div className="pagination-controls mt-3">

                <div className="pagination-container d-flex gap-1">

                    <button
                        className="border rounded p-1"
                        onClick={() => setSessionPageNo(0)}
                        disabled={sessionPageNo === 0}
                    >
                        {'<<'}
                    </button>

                    <button
                        className="border rounded p-1"
                        onClick={() =>
                            setSessionPageNo(
                                Math.max(0, sessionPageNo - 1)
                            )
                        }
                        disabled={sessionPageNo === 0}
                    >
                        {'<'}
                    </button>

                    {renderPageNumbers().map(
                        (page, index) => (

                            <button
                                key={index}
                                className={`border rounded p-1 ${sessionPageNo + 1 === page
                                    ? 'active'
                                    : ''
                                    }`}
                                disabled={
                                    typeof page !== 'number'
                                }
                                onClick={() => {

                                    if (
                                        typeof page === 'number'
                                    ) {
                                        setSessionPageNo(page - 1);
                                    }

                                }}
                            >
                                {page}
                            </button>

                        )
                    )}

                    <button
                        className="border rounded p-1"
                        onClick={() =>
                            setSessionPageNo(sessionPageNo + 1)
                        }
                        disabled={
                            sessionPageNo >= sessionPageCount - 1
                        }
                    >
                        {'>'}
                    </button>

                    <button
                        className="border rounded p-1"
                        onClick={() =>
                            setSessionPageNo(sessionPageCount - 1)
                        }
                        disabled={
                            sessionPageNo >= sessionPageCount - 1
                        }
                    >
                        {'>>'}
                    </button>

                </div>

            </div>
        );

    };

    return (
        <>
            {(loader || paginationLoading) && (
                <div className="candidate-loader-container">
                    <div className="candidate-loader"></div>
                </div>
            )}
            <div className="container my-5">

                <div className="value-add-courses-section mt-5">

                    <div className="d-flex justify-content-between align-items-center mb-3">

                        <h4 className="courses-heading">
                            Career Guidance
                        </h4>

                    </div>
                    <div className="row gy-4 sessions-list">

                        {sessions.map((session) => (

                            <div
                                key={session.id}
                                className="col-12 col-md-6 col-lg-3"
                            >

                                <div className="card h-100 course-card">
                                    <div
                                        style={{
                                            width: '100%',
                                            height: '200px',
                                            overflow: 'hidden'
                                        }}
                                    >
                                        {session.imageUrl ? (
                                            session.imageUrl.startsWith('http') ? (
                                                <Image
                                                    src={session.imageUrl}
                                                    rounded
                                                    style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: 'cover'
                                                    }}
                                                />
                                            ) : (
                                                <CareerGuidanceImage
                                                    imageUrl={session.imageUrl}
                                                    alt={session.headline}
                                                />
                                            )
                                        ) : (
                                            <div
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    background: '#f5f5f5',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    color: '#999',
                                                    fontSize: '12px'
                                                }}
                                            >
                                                No Image
                                            </div>
                                        )}
                                    </div>
                                    <div className="card-body d-flex flex-column">

                                        <h6 className="course-title">
                                            {session.headline}
                                        </h6>


                                        <p className="course-description">
                                            {session.descriptionText}
                                        </p>


                                        <div className="d-flex justify-content-between align-items-center mt-auto">

                                            <span className="course-price">
                                                ₹ {session.price}
                                            </span>


                                            <button
                                                className="btn course-buy-btn"
                                                onClick={() =>
                                                    handleBuy(session)
                                                }
                                                disabled={
                                                    loadingSessionId !== null
                                                }
                                            >
                                                {loadingSessionId === session.id
                                                    ? "Loading..."
                                                    : session.bookingType === "SLOT_BASED"
                                                        ? "Book a Slot"
                                                        : "Book"}
                                            </button>

                                        </div>

                                    </div>

                                </div>

                            </div>

                        ))}

                    </div>
                    {sessions.length === 0 && !loader && (
                        <div className="text-center py-5">
                            No Career Guidance sessions available.
                        </div>
                    )}

                    {renderPagination()}

                </div>

            </div>
            {showCandidateModal && (
                <div
                    className="modal d-block candidate-modal"
                    style={{
                        backgroundColor: "rgba(0,0,0,0.5)"
                    }}
                >
                    <div className="modal-dialog modal-dialog-centered">

                        <div className="modal-content">

                            <div className="modal-header">

                                <div>
                                    <h5 className="modal-title">
                                        Book a Career Guidance Session
                                    </h5>

                                    {selectedSession && (
                                        <small className="text-muted">
                                            {selectedSession.headline}
                                        </small>
                                    )}
                                </div>

                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={closeCandidateModal}
                                />

                            </div>
                            <form onSubmit={handleCandidateSubmit}>

                                <div className="modal-body">

                                    <p className="text-muted mb-4">
                                        Please enter your details to continue.
                                    </p>
                                    <div className="mb-3">

                                        <label className="form-label">
                                            First Name*
                                        </label>

                                        <input
                                            type="text"
                                            className={`form-control ${candidateErrors.firstName
                                                ? "is-invalid"
                                                : ""
                                                }`}
                                            name="firstName"
                                            value={
                                                candidateDetails.firstName
                                            }
                                            onChange={
                                                handleCandidateChange
                                            }
                                            placeholder="Enter first name"
                                        />

                                        {candidateErrors.firstName && (
                                            <div className="invalid-feedback">
                                                {
                                                    candidateErrors.firstName
                                                }
                                            </div>
                                        )}

                                    </div>

                                    <div className="mb-3">

                                        <label className="form-label">
                                            Last Name*
                                        </label>

                                        <input
                                            type="text"
                                            className={`form-control ${candidateErrors.lastName
                                                ? "is-invalid"
                                                : ""
                                                }`}
                                            name="lastName"
                                            value={
                                                candidateDetails.lastName
                                            }
                                            onChange={
                                                handleCandidateChange
                                            }
                                            placeholder="Enter last name"
                                        />

                                        {candidateErrors.lastName && (
                                            <div className="invalid-feedback">
                                                {
                                                    candidateErrors.lastName
                                                }
                                            </div>
                                        )}

                                    </div>


                                    {/* EMAIL */}

                                    <div className="mb-3">

                                        <label className="form-label">
                                            Email*
                                        </label>

                                        <input
                                            type="email"
                                            className={`form-control ${candidateErrors.email
                                                ? "is-invalid"
                                                : ""
                                                }`}
                                            name="email"
                                            value={
                                                candidateDetails.email
                                            }
                                            onChange={
                                                handleCandidateChange
                                            }
                                            placeholder="Enter email address"
                                            autoComplete="off"
                                        />

                                        {candidateErrors.email && (
                                            <div className="invalid-feedback">
                                                {
                                                    candidateErrors.email
                                                }
                                            </div>
                                        )}
                                        <small className="text-muted">
                                            This will be your login ID / username.
                                        </small>

                                    </div>


                                    {/* PHONE */}

                                    <div className="mb-3">

                                        <label className="form-label">
                                            Phone Number*
                                        </label>

                                        <input
                                            type="tel"
                                            className={`form-control ${candidateErrors.phoneNumber
                                                ? "is-invalid"
                                                : ""
                                                }`}
                                            name="phoneNumber"
                                            value={
                                                candidateDetails.phoneNumber
                                            }
                                            onChange={
                                                handleCandidateChange
                                            }
                                            placeholder="Enter 10-digit phone number"
                                            maxLength={13}
                                        />

                                        {candidateErrors.phoneNumber && (
                                            <div className="invalid-feedback">
                                                {
                                                    candidateErrors.phoneNumber
                                                }
                                            </div>
                                        )}

                                    </div>

                                    {/* PASSWORD */}

                                    <div className="mb-3">

                                        <label className="form-label">
                                            Create Password*
                                        </label>

                                        <div className="position-relative">
                                            <input
                                                type={showPassword ? "text" : "password"}
                                                className={`form-control ${candidateErrors.password
                                                    ? "is-invalid"
                                                    : ""
                                                    }`}
                                                name="password"
                                                value={candidateDetails.password}
                                                onChange={handleCandidateChange}
                                                placeholder="Enter password"
                                                autoComplete="new-password"
                                            />

                                            <span
                                                onClick={() => setShowPassword(!showPassword)}
                                                style={{
                                                    position: "absolute",
                                                    right: "15px",
                                                    top: "50%",
                                                    transform: "translateY(-50%)",
                                                    cursor: "pointer",
                                                    color: "#6c757d"
                                                }}
                                            >
                                                <FontAwesomeIcon
                                                    icon={showPassword ? faEyeSlash : faEye}
                                                />
                                            </span>
                                        </div>

                                        {candidateErrors.password && (
                                            <div className="invalid-feedback d-block">
                                                {candidateErrors.password}
                                            </div>
                                        )}
                                        <small className="text-muted">
                                            This will be your login password.
                                        </small>

                                    </div>

                                </div>


                                <div className="modal-footer">

                                    <button
                                        type="button"
                                        className="btn btn-cancel"
                                        onClick={closeCandidateModal}
                                    >
                                        Cancel
                                    </button>

                                    <button
                                        type="submit"
                                        className="btn btn-ok"
                                    >
                                        Continue
                                    </button>

                                </div>

                            </form>

                        </div>

                    </div>

                </div>
            )}
            {showSlotModal && (

                <div
                    className="modal d-block candidate-modal"
                    style={{
                        backgroundColor: "rgba(0,0,0,0.5)"
                    }}
                >

                    <div className="modal-dialog modal-dialog-centered">

                        <div className="modal-content">

                            <div className="modal-header">

                                <div>

                                    <h5>
                                        Select Slot
                                    </h5>

                                </div>

                                <button
                                    className="btn-close"
                                    onClick={closeSlotModal}
                                />

                            </div>


                            <div className="modal-body">

                                {selectedSession && (
                                    <div className="mb-3">

                                        <strong>
                                            {selectedSession.headline}
                                        </strong>

                                        <div className="text-muted">
                                            ₹ {selectedSession.price}
                                        </div>

                                    </div>
                                )}


                                {slots.length === 0 ? (

                                    <p>
                                        No slots available.
                                    </p>

                                ) : (

                                    slots.map((slot) => (

                                        <div
                                            key={slot.id}
                                            className={`border rounded p-3 mb-3 ${selectedSlot?.id === slot.id
                                                ? "border-primary"
                                                : ""
                                                }`}
                                        >

                                            <div className="form-check">

                                                <input
                                                    className="form-check-input"
                                                    type="radio"
                                                    id={`slot-${slot.id}`}
                                                    name="slot"
                                                    checked={
                                                        selectedSlot?.id ===
                                                        slot.id
                                                    }
                                                    onChange={() =>
                                                        setSelectedSlot(slot)
                                                    }
                                                />


                                                <label
                                                    htmlFor={`slot-${slot.id}`}
                                                    className="form-check-label ms-2 w-100"
                                                >

                                                    <h6 className="mb-2">

                                                        {formatDateTime(
                                                            slot.startTime
                                                        )}

                                                    </h6>


                                                    <div className="text-muted">

                                                        Ends:{" "}

                                                        {formatDateTime(
                                                            slot.endTime
                                                        )}

                                                    </div>


                                                    <div className="text-success mt-2">

                                                        Available Seats:{" "}

                                                        {
                                                            slot.availableSeats
                                                        }

                                                    </div>

                                                </label>

                                            </div>

                                        </div>

                                    ))

                                )}

                            </div>


                            <div className="modal-footer">

                                <button
                                    className="btn btn-cancel"
                                    onClick={closeSlotModal}
                                >
                                    Cancel
                                </button>

                                <button
                                    className="btn btn-ok"
                                    disabled={!selectedSlot}
                                    onClick={() => {
                                        setCandidateDetails({
                                            firstName: "",
                                            lastName: "",
                                            email: "",
                                            phoneNumber: "",
                                            password: "",
                                        });

                                        setCandidateErrors({});
                                        setShowSlotModal(false);
                                        setShowCandidateModal(true);
                                    }}
                                >
                                    Proceed to Payment
                                </button>

                            </div>

                        </div>

                    </div>

                </div>

            )}

            {showPaymentSuccessModal && (

                <div
                    className="modal d-block candidate-modal"
                    style={{
                        backgroundColor: "rgba(0,0,0,0.5)"
                    }}
                >

                    <div className="modal-dialog modal-dialog-centered">

                        <div className="modal-content">

                            <div className="modal-header">
                                <h5 className="modal-title">
                                    Payment Successful
                                </h5>

                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setShowPaymentSuccessModal(false)}
                                />
                            </div>

                            <div className="modal-body">
                                <p className="mb-0">
                                    Your session booking has been confirmed.
                                    Please check your email for the confirmation.
                                </p>
                                <p>
                                    You can login using your email ID and the password you created.
                                </p>
                            </div>

                            <div className="modal-footer">

                                <button
                                    type="button"
                                    className="btn btn-ok"
                                    onClick={() =>
                                        setShowPaymentSuccessModal(false)
                                    }
                                >
                                    OK
                                </button>

                            </div>

                        </div>

                    </div>

                </div>

            )}

        </>
    );
};

export default CareerGuidanceHomepage;